import {
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { shift } from '@floating-ui/react-dom'
import { TextTooltip } from '@root/components/InfoPopup'
import { useBusinessContext } from '@root/context'
import { fetchContact, IContact } from '@root/query'
import { useScreen } from '@root/utils'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaMagic, FaPlus, FaTimes } from 'react-icons/fa'
import { keepPreviousData, useQueries } from '@tanstack/react-query'
import styled from 'styled-components'
import ContactsDropdown from './ContactsDropdown'
import { ContactFormModal } from '@root/pages/ContactsPage/ContactFormModal'

const MAX_HEIGHT_DROPDOWN = 300

interface Props {
  value: number[]
  onChange: (contactIds: number[]) => void
  pendingContactName?: string
  onPendingContactDismiss?: () => void
  openOnMount?: boolean
  invalid?: boolean
}

const MultipleContactSelector: React.FC<Props> = ({
  value,
  onChange,
  pendingContactName,
  onPendingContactDismiss,
  openOnMount = false,
  invalid = false
}) => {
  const { slug } = useBusinessContext()
  const { isMobile } = useScreen()
  const [isDropdownOpen, setIsDropdownOpen] = useState(openOnMount)
  const [selectedContact, setSelectedContact] = useState<IContact | undefined>(undefined)
  const [isContactFormOpen, setIsContactFormOpen] = useState(false)
  const fallbackFocusRef = useRef<HTMLButtonElement>(null)

  const contactQueries = useQueries({
    queries: (value || []).map(contactId => ({
      queryKey: [slug, 'contacts', contactId],
      queryFn: () => fetchContact({ slug, contactId }),
      enabled: !!contactId,
      staleTime: 60 * 1000,
      placeholderData: keepPreviousData
    }))
  })

  const selectedContacts = contactQueries.filter(result => result?.data).map(result => result.data)

  const { t } = useTranslation()

  const handleOnSelect = (contactId: number) => {
    const currentIds = value || []
    const exists = currentIds.includes(contactId)

    const newContactIds = exists
      ? currentIds.filter(id => id !== contactId)
      : [...currentIds, contactId]

    onChange?.(newContactIds)
  }

  const handleDismiss = (contactId: number) => {
    const newContactIds = value?.filter(id => id !== contactId) || []
    onChange?.(newContactIds)
  }

  const onEditContact = (contact: IContact) => {
    setIsContactFormOpen(true)
    setIsDropdownOpen(false)
    setSelectedContact(contact)
  }

  const onCreateContact = () => {
    setIsContactFormOpen(true)
    setIsDropdownOpen(false)
    setSelectedContact(undefined)
  }

  const onContactFormClose = () => {
    setIsContactFormOpen(false)
    fallbackFocusRef.current?.focus()
  }

  const onContactFormCreation = (contact: IContact) => {
    handleOnSelect(contact.id)
    setIsContactFormOpen(false)
    setSelectedContact(undefined)
    fallbackFocusRef.current?.focus()
  }

  const { context, refs } = useFloating({
    open: isDropdownOpen,
    onOpenChange: open => {
      setIsDropdownOpen(open)
      if (!open) {
        fallbackFocusRef.current?.focus()
      }
    },
    whileElementsMounted: autoUpdate,
    placement: isMobile ? 'bottom' : 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      shift(),
      size(
        {
          apply: ({ availableWidth, rects, elements }) => {
            Object.assign(elements.floating.style, {
              width: isMobile ? `${availableWidth}px` : `${Math.max(rects.reference.width, 500)}px`,
              maxHeight: `${MAX_HEIGHT_DROPDOWN}px`
            })
          }
        },
        [isMobile]
      )
    ]
  })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  return (
    <>
      <StyledPillRow {...getReferenceProps()} ref={refs.setReference}>
        {pendingContactName && (
          <PendingContactPill>
            <TextTooltip
              tooltip={t('contacts.contactSelector.newContact')}
              placement="bottom-start"
            >
              <PendingContactPillLabel>
                <PendingContactWandIcon aria-hidden>
                  <FaMagic />
                </PendingContactWandIcon>
                <StyledContactName>{pendingContactName}</StyledContactName>
              </PendingContactPillLabel>
            </TextTooltip>
            <StyledRemoveButton
              type="button"
              onClick={e => {
                e.stopPropagation()
                onPendingContactDismiss?.()
              }}
            >
              <FaTimes />
            </StyledRemoveButton>
          </PendingContactPill>
        )}
        {selectedContacts.map(contact => (
          <StyledContactPill key={contact.id}>
            <StyledContactName>{contact.name}</StyledContactName>
            <StyledRemoveButton
              type="button"
              onClick={e => {
                e.stopPropagation()
                handleDismiss(contact.id)
              }}
            >
              <FaTimes />
            </StyledRemoveButton>
          </StyledContactPill>
        ))}

        <StyledAddButton
          ref={fallbackFocusRef}
          type="button"
          $invalid={invalid}
          onClick={() => setIsDropdownOpen(prev => !prev)}
        >
          <FaPlus />
          <span>{t('settings.automation.accountingRules.form.addContact')}</span>
        </StyledAddButton>
      </StyledPillRow>
      {isDropdownOpen && (
        <ContactsDropdown
          context={context}
          refs={refs}
          getFloatingProps={getFloatingProps}
          selectedIds={value || []}
          onContactSelect={handleOnSelect}
          onEditContact={onEditContact}
          onCreateContact={onCreateContact}
        />
      )}
      <ContactFormModal
        contact={selectedContact}
        isVisible={isContactFormOpen}
        handleOnClose={onContactFormClose}
        onCreate={onContactFormCreation}
      />
    </>
  )
}

export default MultipleContactSelector

const StyledPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  min-height: 28px;
`

const StyledContactPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  height: 28px;
  box-sizing: border-box;
  max-width: 240px;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
`

const PendingContactPill = styled(StyledContactPill)`
  background-color: ${({ theme }) => `${theme.colors.AIPurple}11`};
  border-color: ${({ theme }) =>
    `color-mix(in srgb, ${theme.colors.AIPurple} 25%, ${theme.colors.neutralGray})`};
`

const PendingContactPillLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
  flex: 1;
`

const PendingContactWandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.AIPurple};

  svg {
    width: ${({ theme }) => theme.iconSize.xxs}rem;
    height: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

const StyledContactName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.fontSize.px11}rem;
    height: ${({ theme }) => theme.fontSize.px11}rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }
`

const StyledAddButton = styled.button<{ $invalid?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  border: 1px dashed
    ${({ theme, $invalid }) => ($invalid ? theme.colors.error : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  padding: 0 ${({ theme }) => theme.spacing.sm}rem;
  height: 28px;
  box-sizing: border-box;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-family: inherit;

  svg {
    width: ${({ theme }) => theme.fontSize.px11}rem;
    height: ${({ theme }) => theme.fontSize.px11}rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
